import { useState } from 'react'
import { useCVReview } from '../../hooks/useCVReview'
import { useAuth } from '../../contexts/AuthContext'
import { useTranslation } from '../../contexts/LanguageContext'
import { CVReviewModal } from './CVReviewModal'
import { Modal } from '../ui/Modal'
import toast from 'react-hot-toast'
import type { CV } from '../../types/cv'
import type { ReviewSection } from '../../types/api'

interface ReviewButtonProps {
  cv: CV
  onApplyFix?: (section: ReviewSection, field: string | undefined, fix: string, itemId?: string) => Promise<boolean> | boolean
  className?: string
  iconOnly?: boolean
  showConfirmation?: boolean
}

export function ReviewButton({ cv, onApplyFix, className = '', iconOnly = false, showConfirmation = false }: ReviewButtonProps) {
  const { user } = useAuth()
  const { t } = useTranslation()
  const { isReviewing, review, error, startReview } = useCVReview()
  const [showModal, setShowModal] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)

  const handleClick = async () => {
    // If confirmation is needed, show confirmation modal first
    if (showConfirmation && !review) {
      setShowConfirmModal(true)
      return
    }
    await doReview()
  }

  const doReview = async () => {
    setShowConfirmModal(false)

    if (!user) {
      toast.error(t.aiReview.loginToReview, {
        icon: '🔒',
      })
      return
    }

    // If we already have a review, just re-open the modal
    if (review) {
      setShowModal(true)
      return
    }

    // Check if CV has any content
    const hasContent = cv.personalInfo?.fullName ||
      cv.summary ||
      (cv.experience && cv.experience.length > 0) ||
      (cv.education && cv.education.length > 0) ||
      (cv.skills?.technical && cv.skills.technical.length > 0)

    if (!hasContent) {
      toast.error(t.aiReview.addContentFirst, {
        icon: '📝',
      })
      return
    }

    const result = await startReview(cv, cv.settings?.language === 'en' ? 'en' : 'es')
    if (result) {
      setShowModal(true)
    } else if (error) {
      toast.error(error)
    }
  }

  const handleClose = () => {
    setShowModal(false)
    // Don't reset - allow re-opening the modal with previous review
  }

  const handleApplyFix = async (section: ReviewSection, field: string | undefined, fix: string, itemId?: string): Promise<boolean> => {
    if (onApplyFix) {
      return await onApplyFix(section, field, fix, itemId)
    }
    return false
  }

  const iconSize = iconOnly ? 'w-6 h-6' : 'w-3.5 h-3.5'

  return (
    <>
      <button
        onClick={handleClick}
        disabled={isReviewing}
        className={`inline-flex items-center justify-center gap-1.5 font-medium transition-colors ${
          iconOnly
            ? 'rounded-full'
            : 'px-3 py-1.5 text-xs rounded-lg border min-h-[44px] lg:min-h-0'
        } ${
          isReviewing
            ? 'bg-slate-100 border-slate-200 text-slate-400 cursor-wait'
            : iconOnly
              ? 'bg-gradient-to-r from-violet-500 to-purple-500 text-white shadow-lg hover:from-violet-600 hover:to-purple-600'
              : 'bg-gradient-to-r from-violet-500/10 to-purple-500/10 border-violet-200 text-violet-700 hover:from-violet-500/20 hover:to-purple-500/20 hover:border-violet-300'
        } ${className}`}
        title={user ? t.aiReview.reviewWithAITitle : t.aiReview.loginToReviewTitle}
      >
        {isReviewing ? (
          <svg
            className={`${iconSize} animate-spin`}
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        ) : (
          <svg
            className={iconSize}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
            />
          </svg>
        )}
        {!iconOnly && !isReviewing && <span>{t.aiReview.reviewCV}</span>}
        {!iconOnly && isReviewing && <span>{t.aiReview.reviewing}</span>}
      </button>

      {/* Confirmation Modal */}
      <Modal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title={t.aiReview.reviewWithAITitle}
      >
        <div className="text-center py-4">
          <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-r from-violet-500 to-purple-500 rounded-full flex items-center justify-center">
            <svg
              className="w-8 h-8 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
              />
            </svg>
          </div>
          <p className="text-gray-600 mb-6">
            {t.aiReview.aiWillAnalyze}
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => setShowConfirmModal(false)}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              {t.common.cancel}
            </button>
            <button
              onClick={doReview}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-violet-500 to-purple-500 rounded-lg hover:from-violet-600 hover:to-purple-600 transition-colors"
            >
              {t.aiReview.reviewNow}
            </button>
          </div>
        </div>
      </Modal>

      {review && (
        <CVReviewModal
          isOpen={showModal}
          onClose={handleClose}
          review={review}
          onApplyFix={handleApplyFix}
        />
      )}
    </>
  )
}
